import { type FC, useState } from "react";
import Button from "@mui/material/Button";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import { useSnackbar } from "notistack";
import { useSetContractPaymentStatusMutation } from "@/admin/entities/contractPayments";
import { MarkContractPaymentPaidDialog } from "./MarkContractPaymentPaidDialog";
import { IssueInvoiceButton } from "./IssueInvoiceButton";
import { ReissueInvoiceDialog } from "./ReissueInvoiceDialog";
import { SendInvoiceButton } from "./SendInvoiceButton";

/** Минимум платежа для действий: строка списка и карточка платежа подходят обе. */
export interface ContractPaymentActionsPayment {
  id: string;
  status: string;
  invoice?: { sentAt?: string | null; sentTo: string[] } | null;
}

interface ContractPaymentActionsProps {
  payment: ContractPaymentActionsPayment;
  /** Зовётся после любого успешного действия: карточка перезапрашивает себя и список. */
  onCompleted?: () => void;
  /** Текст вместо пустоты, когда у статуса нет действий (карточка платежа). */
  emptyText?: string;
}

/** Статусы, из которых доступно перевыставление уже выставленного счёта. */
const CAN_REISSUE_INVOICE = ["wait_payment", "on_way"];

/**
 * Статусы, из которых платёж с уже выставленным счётом возвращается в
 * ожидание оплаты.
 *
 * Четвёртая волна ревью, находка 1: без этого перехода платёж со счётом на
 * паузе оказывался в тупике. Перевыставление доступно только из
 * wait_payment и on_way, закрытие месяца и корректировка отчёта такой
 * платёж отвергают (счёт пересобрать нечем), и починить это можно было
 * только руками в базе. Обратно в pending выход не помогает: из pending со
 * счётом кнопок в wait_payment тоже не было, а пересборка расписания при
 * правке контракта такой платёж отклоняет.
 */
const CAN_RETURN_TO_WAIT_PAYMENT = ["paused", "pending"];

/**
 * Действия над платежом по его статусу (спека §5). Один статус это один
 * набор кнопок, без ветвлений в разметке: набор берётся из таблицы, а сама
 * таблица повторяет разрешённые переходы бэкенда.
 */
// Финальное ревью, M4/задача 7: ручной переход в wait_payment раньше был
// доступен и без счёта (кнопка "Инвойс выставлен" просто меняла статус).
// После этого перехода автовыставление платёж уже не видит
// (findPendingWithoutInvoice берёт только pending), и счёт по нему не
// появится никогда. Поэтому у платежа без счёта путь в wait_payment один,
// через IssueInvoiceButton: он делает переход и выставляет счёт одной
// операцией. У платежа со счётом терять уже нечего, и ему переход
// возвращается отдельно, см. CAN_RETURN_TO_WAIT_PAYMENT.
const ACTIONS_BY_STATUS: Record<string, { status: string; label: string }[]> = {
  draft: [
    { status: "pending", label: "К выставлению" },
    { status: "paused", label: "Пауза" },
    { status: "canceled", label: "Отменить" },
    { status: "lost", label: "Списать" },
  ],
  pending: [
    { status: "paused", label: "Пауза" },
    { status: "canceled", label: "Отменить" },
    { status: "lost", label: "Списать" },
  ],
  wait_payment: [
    { status: "on_way", label: "Клиент оплатил, ждём" },
    { status: "paused", label: "Пауза" },
    { status: "canceled", label: "Отменить" },
    { status: "lost", label: "Списать" },
  ],
  on_way: [
    { status: "paused", label: "Пауза" },
    { status: "canceled", label: "Отменить" },
    { status: "lost", label: "Списать" },
  ],
  paused: [
    { status: "pending", label: "К выставлению" },
    { status: "canceled", label: "Отменить" },
    { status: "lost", label: "Списать" },
  ],
  paid: [],
  canceled: [],
  lost: [],
};

/** Статусы, из которых доступна кнопка «Оплачен» с датой. */
const CAN_MARK_PAID = ["wait_payment", "on_way"];

export const ContractPaymentActions: FC<ContractPaymentActionsProps> = ({
  payment,
  onCompleted,
  emptyText,
}) => {
  const paymentId = payment.id;
  const status = payment.status;
  const { enqueueSnackbar } = useSnackbar();
  const [paidOpen, setPaidOpen] = useState(false);
  const [reissueOpen, setReissueOpen] = useState(false);

  const [setStatus, { loading }] = useSetContractPaymentStatusMutation({
    onCompleted: () => {
      enqueueSnackbar("Статус платежа обновлён", { variant: "success" });
      onCompleted?.();
    },
    onError: (error) => {
      enqueueSnackbar(error.message, { variant: "error" });
    },
  });

  const hasInvoice = Boolean(payment.invoice);
  const actions = [
    // Возврат в ожидание оплаты идёт первым: это выход из тупика, и он
    // должен быть виден раньше отмены и списания.
    ...(hasInvoice && CAN_RETURN_TO_WAIT_PAYMENT.includes(status)
      ? [{ status: "wait_payment", label: "Ждём оплату" }]
      : []),
    ...(ACTIONS_BY_STATUS[status] ?? []),
  ];
  // Выставить можно только у pending без счёта, см. резолвер мутации
  // (CONTRACT_PAYMENT_INVOICE_NOT_ISSUABLE).
  const canIssue = status === "pending" && !hasInvoice;
  const canReissue = hasInvoice && CAN_REISSUE_INVOICE.includes(status);
  // Письмо не отправить по оплаченному или отменённому платежу (мутация
  // отвергает эти статусы своим кодом, финальное ревью M6): счёт к оплате
  // по уже закрытой истории вводит клиента в заблуждение.
  const canSend = hasInvoice && status !== "paid" && status !== "canceled";

  if (
    actions.length === 0 &&
    !CAN_MARK_PAID.includes(status) &&
    !canIssue &&
    !canReissue &&
    !canSend
  ) {
    return emptyText ? (
      <Typography variant="body2" color="text.secondary">
        {emptyText}
      </Typography>
    ) : null;
  }

  return (
    <>
      <Stack
        direction="row"
        spacing={1}
        flexWrap="wrap"
        rowGap={1}
        alignItems="flex-start"
      >
        {CAN_MARK_PAID.includes(status) && (
          <Button
            size="small"
            variant="contained"
            disabled={loading}
            onClick={(event) => {
              event.stopPropagation();
              setPaidOpen(true);
            }}
          >
            Оплачен
          </Button>
        )}
        {actions.map((action) => (
          <Button
            key={action.status}
            size="small"
            disabled={loading}
            onClick={(event) => {
              event.stopPropagation();
              void setStatus({
                variables: { id: paymentId, status: action.status },
              });
            }}
          >
            {action.label}
          </Button>
        ))}
        {canIssue && (
          <IssueInvoiceButton paymentId={paymentId} onCompleted={onCompleted} />
        )}
        {canReissue && (
          <Button
            size="small"
            onClick={(event) => {
              event.stopPropagation();
              setReissueOpen(true);
            }}
          >
            Перевыставить
          </Button>
        )}
        {canSend && (
          <SendInvoiceButton
            paymentId={paymentId}
            sentAt={payment.invoice?.sentAt}
            sentTo={payment.invoice?.sentTo ?? []}
            onCompleted={onCompleted}
          />
        )}
      </Stack>
      <MarkContractPaymentPaidDialog
        open={paidOpen}
        paymentId={paymentId}
        onClose={() => setPaidOpen(false)}
        onCompleted={onCompleted}
      />
      <ReissueInvoiceDialog
        open={reissueOpen}
        paymentId={paymentId}
        onClose={() => setReissueOpen(false)}
        onCompleted={onCompleted}
      />
    </>
  );
};
